import { PropsWithChildren, createContext, useContext, useMemo } from "react";

import { Role } from "@/apollo/types";

import { AppAction, PERMISSION_MAP, Permission } from "./permissions";

type RoleContextValue = {
  role: Role;
  permissions: Permission[];
  /** Check a semantic UI action against the user's permissions. */
  can: (action: AppAction) => boolean;
  /** Raw permission check — prefer `can()` for UI logic. */
  hasPermission: (permission: Permission) => boolean;
};

const RoleContext = createContext<RoleContextValue | null>(null);

type RoleProviderProps = PropsWithChildren<{
  role: Role;
  /** Raw permission strings from the server (e.g. "create:Connection"). */
  permissions: string[];
}>;

/**
 * Provides role + permission data to the subtree.
 * Rendered inside AccountProvider, which supplies role and permissions
 * from a single merged query.
 */
export function RoleProvider({
  role,
  permissions: rawPermissions,
  children,
}: RoleProviderProps) {
  const permissions = useMemo(
    () => rawPermissions as Permission[],
    [rawPermissions],
  );

  const permSet = useMemo(() => new Set(permissions), [permissions]);
  const isAdmin = permSet.has("manage:all");

  const hasPermission = useMemo(
    () =>
      (permission: Permission): boolean => {
        if (isAdmin || permSet.has(permission)) return true;
        // `manage:Subject` is a CASL wildcard — it grants any action on that subject.
        const subject = permission.split(":")[1];
        return subject ? permSet.has(`manage:${subject}` as Permission) : false;
      },
    [isAdmin, permSet],
  );

  const can = useMemo(
    () =>
      (action: AppAction): boolean =>
        PERMISSION_MAP[action].some(hasPermission),
    [hasPermission],
  );

  const value = useMemo(
    () => ({ role, permissions, can, hasPermission }),
    [role, permissions, can, hasPermission],
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function useRole(): RoleContextValue {
  const ctx = useContext(RoleContext);
  if (!ctx) {
    throw new Error("useRole must be used within a RoleProvider");
  }
  return ctx;
}
